import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { FlexRow } from 'ui-components/components/flex/row';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import style from './wizard-step.module.less';

const wizardStepMessages = defineMessages({
  editButton: {
    id: 'r2xfTn',
    description: 'Edit button label on a completed wizard step',
    defaultMessage: 'Edit',
  },
});

type WizardStepProps = {
  stepNumber: number;
  title: string;
  isActive: boolean;
  isCompleted: boolean;
  onEdit?: () => void;
  children: ReactNode;
  collapsedSummary?: ReactNode;
};

export const WizardStep = ({ stepNumber, title, isActive, isCompleted, onEdit, children, collapsedSummary }: WizardStepProps) => {
  const handleEdit = useCallback(() => {
    onEdit?.();
  }, [onEdit]);

  const renderBadge = () => {
    if (isCompleted) {
      return (
        <div className={style['step-badge-done']}>
          <FontAwesomeIcon icon={faCheckCircle} />
        </div>
      );
    }
    if (isActive) {
      return (
        <div className={style['step-badge-active']}>
          <Text textType={TextType.Caption} value={String(stepNumber)} />
        </div>
      );
    }
    return (
      <div className={style['step-badge-pending']}>
        <Text textType={TextType.Caption} value={String(stepNumber)} />
      </div>
    );
  };

  return (
    <div className={style['wizard-step']}>
      <FlexRow className={style['wizard-step-header']}>
        <FlexRow className={style['wizard-step-header-left']}>
          {renderBadge()}
          <Text isBold={isActive || isCompleted} textType={TextType.Paragraph} value={title} />
        </FlexRow>
        {isCompleted && onEdit && (
          <Button onClick={handleEdit} theme={THEME_CONSTANTS.link}>
            <FormattedMessage {...wizardStepMessages.editButton} />
          </Button>
        )}
      </FlexRow>

      {isActive && <div className={style['step-content']}>{children}</div>}

      {isCompleted && !isActive && collapsedSummary && <div className={style['step-collapsed-summary']}>{collapsedSummary}</div>}
    </div>
  );
};
